import * as React from 'react';
import { Command } from 'cmdk';
import { Check, ChevronsUpDown, Search } from 'lucide-react';

import { cn } from '@repo/utils';
import { Button } from '../../../components/ui/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/Popover';
import { useDebounce } from '../../../lib/hooks';
import { useCustomers } from '../hooks';

interface CustomerPickerProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Company-wide customer search picker -- unlike OrgUnitPicker (a small, fully-loaded tree),
 * the customer list can be large, so this searches server-side via useCustomers rather than
 * filtering a pre-fetched list client-side.
 */
export function CustomerPicker({ value, onChange, placeholder = 'Select customer...', disabled }: CustomerPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data, isFetching } = useCustomers({ page: 1, limit: 20, query: debouncedSearch || undefined });
  const customers = data?.data ?? [];

  // The selected customer may not be in the current search results -- keep its label available
  // once picked so re-opening the picker doesn't blank out the trigger's display text.
  const [selectedLabel, setSelectedLabel] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!value) {
      setSelectedLabel(null);
      return;
    }
    const match = customers.find((c) => c.id === value);
    if (match) setSelectedLabel(match.mobile ? `${match.name} (${match.mobile})` : match.name);
  }, [value, customers]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-surface-3 font-normal"
          disabled={disabled}
        >
          {value ? (
            <span className="truncate">{selectedLabel || value}</span>
          ) : (
            <span className="text-text-muted">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false} className="flex flex-col">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
            <Search className="h-4 w-4 text-text-muted shrink-0" />
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder="Search by name or mobile..."
              className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-muted outline-none h-8"
            />
          </div>
          <Command.List className="max-h-[300px] overflow-y-auto p-1">
            <Command.Empty className="py-6 text-center text-sm text-text-muted">
              {isFetching ? 'Searching...' : 'No customer found.'}
            </Command.Empty>
            {customers.map((c) => (
              <Command.Item
                key={c.id}
                value={c.id}
                onSelect={() => {
                  onChange(c.id);
                  setOpen(false);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(c.id);
                  setOpen(false);
                }}
                className={cn(
                  'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
                  'aria-selected:bg-accent-500/10 aria-selected:text-accent-500',
                )}
              >
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium">{c.name}</span>
                  <span className="text-xs text-text-muted">{c.mobile || c.customerCode || ''}</span>
                </div>
                <Check
                  className={cn(
                    'ml-auto h-4 w-4',
                    value === c.id ? 'opacity-100' : 'opacity-0',
                  )}
                />
              </Command.Item>
            ))}
          </Command.List>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
